import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authStorage } from '@/lib/auth-storage';
import { authService } from '@/services/auth';
import type { ActiveNetworkSummary } from '@just-frotas/shared';
import { useAuth } from './AuthContext';

// Contexto de NAVEGACAO de rede (paralelo a ActiveClientContext).
// Lista as redes acessiveis ao user e mantem activeNetworkId em sync.
//
// Visibilidade:
// - super_admin: todas as redes
// - rede_master: redes do cluster master (crd_rede.crd_rdm_id)
// - rede_regular: a propria rede
// - cliente sem rede: lista vazia (header esconde section de rede)

interface NavigationContextValue {
  redes: ActiveNetworkSummary[];
  activeNetworkId: number | null;
  active: ActiveNetworkSummary | null;
  loading: boolean;
  reload: () => Promise<void>;
  switchNetwork: (networkId: number) => Promise<void>;
}

const NavigationContext = createContext<NavigationContextValue | undefined>(undefined);

interface ProviderProps {
  children: ReactNode;
}

export function NavigationProvider({ children }: ProviderProps) {
  const { user, refreshMe } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [redes, setRedes] = useState<ActiveNetworkSummary[]>([]);
  const [activeNetworkId, setActiveNetworkId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!user) {
      setRedes([]);
      setActiveNetworkId(null);
      return;
    }
    setLoading(true);
    try {
      const resp = await authService.listRedesAcessiveis();
      setRedes(resp.redes);
      setActiveNetworkId(resp.active);
    } catch {
      // Falha silenciosa (user sem rede acessivel = lista vazia, OK).
      setRedes([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    reload().catch(() => {
      /* silencioso */
    });
  }, [reload]);

  const switchNetwork = useCallback(
    async (networkId: number) => {
      const resp = await authService.switchActiveNetwork(networkId);
      authStorage.setToken(resp.accessToken);
      setActiveNetworkId(resp.activeNetwork.id);
      // /auth/me reflete activeContext.rede atualizado apos switch.
      await refreshMe();
      // Reset total de cache: tudo que estava carregado pertencia ao
      // contexto anterior. Sem clear, telas continuam mostrando dados
      // stale e novos clicks podem disparar 404 (tenant scope do backend
      // rejeita recurso de outro cliente/rede).
      queryClient.clear();
      // Volta pra dashboard - simula reinicio de sessao (UX pretendida).
      navigate('/', { replace: true });
    },
    [refreshMe, queryClient, navigate],
  );

  const active = useMemo(
    () => redes.find((r) => r.id === activeNetworkId) ?? null,
    [redes, activeNetworkId],
  );

  const value = useMemo<NavigationContextValue>(
    () => ({ redes, activeNetworkId, active, loading, reload, switchNetwork }),
    [redes, activeNetworkId, active, loading, reload, switchNetwork],
  );

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

export function useNavigation(): NavigationContextValue {
  const ctx = useContext(NavigationContext);
  if (!ctx) throw new Error('useNavigation deve ser usado dentro de <NavigationProvider>');
  return ctx;
}
